// BOM resolution, shared by the browser (editor, dashboard, item panel) and the server (MCP, rules).
//
// A BOM family belongs to a Shopify product (one BOM for all its variants) or to a sub-assembly; each
// `bom` row is one revision. The family resolves to a flat BOM per variant:
// - a line applies to the variants whose options match its `applies_when` ({} = every variant)
// - a line with `option_name` picks its component from bom_option_map by the variant's value for that
//   option (falling back to the line's own component)
// - a bom_override changes one variant's quantity (0 leaves the line out) and/or component
// - sub-assemblies with a BOM of their own: phantom ones are exploded into their parts (their lines see
//   the same variant options), stocked ones stay one line costed at their own roll-up
// Costs: a component with a BOM costs its roll-up, otherwise its std_cost, otherwise its preferred
// vendor's price. Anything without a cost is reported (`missing`), never counted as zero. Charges --- a family's
// charges (build_charge: what its maker charges, shared/charges.ts) count with its lines, a phantom's with its parts;
// per-order ones only in `perOrder` (a roll-up has no order quantity).
// Also here: buildable quantities from stock (`buildable`), comparing two resolutions (`compareResolved`)
// and where a part can be replaced (`replaceTargets`).

import type { ChargeLayer, ChargePer } from "./charges.ts";
import { planToday } from "./day.ts";
import type { Bom, BomLine, BomOptionMap, BomOverride, BuildCharge, Item, Location, ShopifyProduct, StockLevel, VendorItem } from "./db.gen.ts";

export type BomItem = Pick<
  Item,
  | "id"
  | "kind"
  | "source"
  | "sku"
  | "name"
  | "std_cost"
  | "price"
  | "lifecycle"
  | "archived"
  | "project_id"
  | "tags"
  | "procurement"
  | "shopify_product_id"
  | "shopify_product_title"
  | "shopify_variant_title"
  | "shopify_options"
> &
  Partial<Pick<Item, "part_type" | "pack_type" | "pack_print">>; // Packaging --- a packaging part's lines are packaging lines (isPackagingLine)
// Manufacturing projects --- a revision's module order and a line's module are optional here, so rows built by hand
// (tests, make planning) needn't carry them; resolution ignores both (shared/projects.ts groups by them).
export type BomRow = Pick<
  Bom,
  "id" | "shopify_product_id" | "item_id" | "revision" | "status" | "effective_from" | "project_ids" | "make_vendor_id" | "phantom" | "created_at"
> &
  Partial<Pick<Bom, "section_order">>;
export type BomLineRow = Omit<BomLine, "updated_at" | "section"> & Partial<Pick<BomLine, "section">>;

/**
 * Packaging --- a packaging line: its part is a packaging part (part_type 'packaging'), or the line says so
 * (bom_line.is_packaging: a part that's packaging only here, like screwdrivers in the box). The cost stack's
 * packaging layer (resolve()'s `packaging`) and the BOM's Packaging group (sectionsOf()) both follow it.
 */
export const isPackagingLine = (line: Pick<BomLineRow, "is_packaging">, part: Pick<BomItem, "part_type"> | undefined | null) =>
  line.is_packaging || part?.part_type === "packaging";

/** Every part a line can put in a build: its own, the ones an option picks and the ones a variant's override uses. */
export function linePartIds(ix: BomIndex, line: BomLineRow): string[] {
  const ids = [line.component_item_id, ...(ix.mapsByLine.get(line.id)?.values() ?? [])];
  for (const o of ix.overridesByLine.get(line.id)?.values() ?? []) ids.push(o.component_item_id);
  return [...new Set(ids.filter((id): id is string => !!id))];
}

/** The same for a line as written, before a variant picks its part: counted as packaging, or any part it can use is. */
export function isPackagingRow(ix: BomIndex, line: BomLineRow): boolean {
  return line.is_packaging || linePartIds(ix, line).some((id) => ix.itemById.get(id)?.part_type === "packaging");
}
export type BomVendorLink = Pick<VendorItem, "item_id" | "unit_price" | "price_breaks" | "is_preferred">;
export type Supply = BomLine["supply"];
export type Options = Record<string, string>;
export type Conditions = Record<string, string[]>;

export type BomData = {
  items: readonly BomItem[];
  boms: readonly BomRow[];
  lines: readonly BomLineRow[];
  maps: readonly Pick<BomOptionMap, "id" | "bom_line_id" | "option_value" | "component_item_id">[];
  overrides: readonly Pick<BomOverride, "id" | "bom_line_id" | "variant_item_id" | "qty" | "component_item_id">[];
  vendorItems: readonly BomVendorLink[];
  /** For product titles when a product has no variants left. */
  products?: readonly Pick<ShopifyProduct, "id" | "title">[];
  /** Stock to build with per item (rows add up): at HHL, Shopify items' available and the ledger's balance; see onHandFor(). */
  stock?: readonly { item_id: string; on_hand: number | string }[];
  /** YYYY-MM-DD; decides which active revision is current. Defaults to today (UTC). */
  today?: string;
  /** Charges --- every family's charges (build_charge). */
  charges?: readonly Pick<BuildCharge, "id" | "shopify_product_id" | "item_id" | "position" | "name" | "amount" | "per" | "layer">[];
};

/**
 * The stock rows that count for building: HHL's own locations in use. Consignment --- stock at a CM site (Tailhoo's
 * Shopify location too) is the CM's, read by the Consignment page and make orders, never as HHL's on hand.
 */
export function countedStock(
  levels: readonly Pick<StockLevel, "item_id" | "location_id" | "on_hand">[],
  locations: readonly Pick<Location, "id" | "kind" | "active" | "ignored">[],
) {
  const counts = new Set(locations.filter((l) => l.active && !l.ignored && l.kind === "hhl").map((l) => l.id));
  return levels.filter((s) => counts.has(s.location_id));
}

export const SUPPLY_LABELS: Record<Supply, string> = { hhl_consigns: "HHL sends", cm_buys: "CM buys", cm_makes: "CM makes" };
/**
 * Whether the CM provides it, buying it or making it in-house: HHL doesn't buy, send or plan it, and its cost is in the
 * CM's price. "CM makes" also builds a sub-assembly inside this build (`builtHere`).
 */
export const byCm = (supply: Supply) => supply !== "hhl_consigns";
/**
 * Whether a sub-assembly is built inside what uses it, in the same order, rather than on its own make PO (Mitch,
 * 2026-11-01: "Do we really need to specify this?"): it has no maker of its own, or the same maker as what it goes in
 * (`parentMaker`), or it's on a "CM makes" line (or an old phantom). One a different maker builds is built on its own
 * and shipped over. Nothing to set: who makes what says it.
 */
export const builtInside = (sub: Pick<BomRow, "phantom" | "make_vendor_id">, parentMaker: string | null, supply: Supply) =>
  supply === "cm_makes" || sub.phantom || !sub.make_vendor_id || sub.make_vendor_id === parentMaker;
/** A sub-assembly built inside what uses it (its parts keep their own supply): see builtInside(). */
const builtHere = (sub: BomRow | undefined, item: BomItem, supply: Supply, maker: string | null) =>
  !!sub && item.source === "local" && builtInside(sub, maker, supply);

const norm = (s: unknown) =>
  String(s ?? "")
    .trim()
    .toLowerCase();
const num = (v: unknown) => (v == null || v === "" ? null : Number(v));
const bySku = (a: { sku: string }, b: { sku: string }) => a.sku.localeCompare(b.sku, undefined, { numeric: true, sensitivity: "base" });
const dateText = (v: unknown) => (v == null ? null : String(v).slice(0, 10));

/** The family a revision belongs to: "product:<gid>" or "item:<uuid>". */
export const familyKey = (bom: Pick<Bom, "shopify_product_id" | "item_id">) =>
  bom.shopify_product_id ? `product:${bom.shopify_product_id}` : `item:${bom.item_id}`;

export type BomIndex = ReturnType<typeof indexBoms>;

/** Lookups for resolving; build once per change of the underlying rows. */
export function indexBoms(data: BomData) {
  const today = data.today ?? planToday();
  const itemById = new Map(data.items.map((i) => [i.id, i]));
  const bomById = new Map(data.boms.map((b) => [b.id, b]));
  const productTitle = new Map((data.products ?? []).map((p) => [p.id, p.title]));

  const variantsByProduct = new Map<string, BomItem[]>();
  for (const i of data.items) {
    if (i.source !== "shopify" || !i.shopify_product_id || i.archived) continue;
    let list = variantsByProduct.get(i.shopify_product_id);
    if (!list) variantsByProduct.set(i.shopify_product_id, (list = []));
    list.push(i);
    if (!productTitle.has(i.shopify_product_id)) productTitle.set(i.shopify_product_id, i.shopify_product_title);
  }
  for (const list of variantsByProduct.values()) list.sort(bySku);

  const families = new Map<string, BomRow[]>();
  for (const b of data.boms) {
    const key = familyKey(b);
    let list = families.get(key);
    if (!list) families.set(key, (list = []));
    list.push(b);
  }
  for (const list of families.values()) list.sort((a, b) => b.revision - a.revision);

  const linesByBom = new Map<string, BomLineRow[]>();
  for (const l of data.lines) {
    let list = linesByBom.get(l.bom_id);
    if (!list) linesByBom.set(l.bom_id, (list = []));
    list.push(l);
  }
  for (const list of linesByBom.values())
    list.sort((a, b) => a.position - b.position || (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : 0));

  const mapsByLine = new Map<string, Map<string, string>>(); // line → lower(value) → component
  for (const m of data.maps) {
    let map = mapsByLine.get(m.bom_line_id);
    if (!map) mapsByLine.set(m.bom_line_id, (map = new Map()));
    map.set(norm(m.option_value), m.component_item_id);
  }

  const overridesByLine = new Map<string, Map<string, BomData["overrides"][number]>>(); // line → variant → override
  for (const o of data.overrides) {
    let map = overridesByLine.get(o.bom_line_id);
    if (!map) overridesByLine.set(o.bom_line_id, (map = new Map()));
    map.set(o.variant_item_id, o);
  }

  // The preferred vendor's price (unit price, else its first price break), else any vendor's.
  const vendorPrice = new Map<string, number>();
  const hasVendor = new Set<string>();
  const priceOf = (l: BomVendorLink) => num(l.unit_price) ?? num((l.price_breaks as { unit_price: number }[] | null)?.[0]?.unit_price);
  for (const l of [...data.vendorItems].sort((a, b) => Number(b.is_preferred) - Number(a.is_preferred))) {
    hasVendor.add(l.item_id);
    const p = priceOf(l);
    if (p != null && !vendorPrice.has(l.item_id)) vendorPrice.set(l.item_id, p);
  }

  const onHand = new Map<string, number>();
  for (const s of data.stock ?? []) onHand.set(s.item_id, (onHand.get(s.item_id) ?? 0) + Number(s.on_hand));

  // Charges --- by family, in order.
  const chargesByFamily = new Map<string, NonNullable<BomData["charges"]>[number][]>();
  for (const c of data.charges ?? []) {
    const key = familyKey(c);
    chargesByFamily.set(key, [...(chargesByFamily.get(key) ?? []), c]);
  }
  for (const list of chargesByFamily.values()) list.sort((a, b) => a.position - b.position);

  return {
    today,
    itemById,
    bomById,
    productTitle,
    variantsByProduct,
    families,
    linesByBom,
    mapsByLine,
    overridesByLine,
    vendorPrice,
    hasVendor,
    onHand,
    chargesByFamily,
    rollups: new Map<string, Rollup>(),
  };
}

// Revisions --------------------------------------------------------------------------------------

/** Active revisions in effect today, newest first. */
const inEffect = (ix: BomIndex, revs: BomRow[]) =>
  revs
    .filter((b) => b.status === "active" && (dateText(b.effective_from) ?? "") <= ix.today)
    .sort((a, b) => (dateText(b.effective_from) ?? "").localeCompare(dateText(a.effective_from) ?? "") || b.revision - a.revision);

/**
 * The revision a family uses now: the active one in effect (latest effective_from up to today), else
 * the newest draft (so BOMs being set up still cost and explode), else one that takes effect later.
 */
export function currentRevision(ix: BomIndex, key: string): BomRow | undefined {
  const revs = ix.families.get(key) ?? [];
  return inEffect(ix, revs)[0] ?? revs.find((b) => b.status === "draft") ?? revs.find((b) => b.status === "active");
}

/** "Active", "Draft", "Retired", or "Superseded" for an active revision a newer one replaced. */
export function revisionState(ix: BomIndex, bom: BomRow): "active" | "draft" | "retired" | "superseded" | "scheduled" {
  if (bom.status !== "active") return bom.status;
  if ((dateText(bom.effective_from) ?? "") > ix.today) return "scheduled";
  return inEffect(ix, ix.families.get(familyKey(bom)) ?? [])[0]?.id === bom.id ? "active" : "superseded";
}

/** The BOM a component explodes or costs through: a sub-assembly's own, or a sell variant's product's. */
export function bomOf(ix: BomIndex, item: BomItem): BomRow | undefined {
  return currentRevision(ix, item.source === "shopify" && item.shopify_product_id ? `product:${item.shopify_product_id}` : `item:${item.id}`);
}

export function ownerLabel(ix: BomIndex, bom: Pick<Bom, "shopify_product_id" | "item_id">): string {
  if (bom.shopify_product_id) return ix.productTitle.get(bom.shopify_product_id) || bom.shopify_product_id;
  const item = ix.itemById.get(bom.item_id ?? "");
  return item ? item.name : "Unknown item";
}

/** The variants a family is built in: a product's live variants, or the sub-assembly itself. */
export function variantsOf(ix: BomIndex, bom: Pick<Bom, "shopify_product_id" | "item_id">): BomItem[] {
  if (bom.shopify_product_id) return ix.variantsByProduct.get(bom.shopify_product_id) ?? [];
  const item = ix.itemById.get(bom.item_id ?? "");
  return item ? [item] : [];
}

// Options and conditions -------------------------------------------------------------------------

export function variantOptions(item: Pick<Item, "shopify_options"> | undefined): Options {
  const raw = item?.shopify_options;
  if (!raw || typeof raw !== "object") return {};
  return Object.fromEntries(Object.entries(raw as Record<string, unknown>).map(([k, v]) => [k, String(v)]));
}

/** A product's options in Shopify's order, each with its values in variant order. */
export function productOptions(variants: readonly BomItem[]): { name: string; values: string[] }[] {
  const names = new Map<string, { name: string; values: string[]; at: number }>();
  for (const v of variants) {
    const opts = variantOptions(v);
    for (const [name, value] of Object.entries(opts)) {
      let o = names.get(name);
      // jsonb doesn't keep key order; the variant title ("Wireless / Ghost Black") does.
      if (!o) names.set(name, (o = { name, values: [], at: v.shopify_variant_title.indexOf(value) }));
      if (!o.values.includes(value)) o.values.push(value);
    }
  }
  return [...names.values()].sort((a, b) => (a.at < 0 ? 1e9 : a.at) - (b.at < 0 ? 1e9 : b.at)).map(({ name, values }) => ({ name, values }));
}

export function conditionsOf(line: Pick<BomLine, "applies_when">): Conditions {
  const raw = line.applies_when;
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Conditions = {};
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) if (Array.isArray(v) && v.length) out[k] = v.map(String);
  return out;
}

function optionValue(options: Options, name: string): string | undefined {
  const want = norm(name);
  for (const [k, v] of Object.entries(options)) if (norm(k) === want) return v;
  return undefined;
}

/** Every option a line lists must have one of its values (case doesn't matter). */
export function lineApplies(line: Pick<BomLine, "applies_when">, options: Options): boolean {
  for (const [name, values] of Object.entries(conditionsOf(line))) {
    const value = optionValue(options, name);
    if (value === undefined || !values.some((v) => norm(v) === norm(value))) return false;
  }
  return true;
}

/** "Connection type = Wireless; Color = Ghost Black, Atomic Purple" */
export function formatConditions(c: Conditions): string {
  return Object.entries(c)
    .map(([name, values]) => `${name} = ${values.join(", ")}`)
    .join("; ");
}

/** Parses formatConditions' text, matching names and values to the product's options when given. */
export function parseConditions(text: string, known?: { name: string; values: string[] }[]): { ok: true; value: Conditions } | { ok: false; error: string } {
  const out: Conditions = {};
  for (const part of text
    .split(/[;\n]/)
    .map((p) => p.trim())
    .filter(Boolean)) {
    const m = part.match(/^([^=]+?)\s*(?:=|:|is)\s*(.+)$/i);
    if (!m) return { ok: false, error: `Write conditions like "Connection type = Wireless" (got "${part}")` };
    let name = m[1].trim();
    let values = m[2]
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
    if (known?.length) {
      const opt = known.find((o) => norm(o.name) === norm(name));
      if (!opt) return { ok: false, error: `No option called "${name}"; options are ${known.map((o) => o.name).join(", ")}` };
      name = opt.name;
      // Values may contain commas' neighbours like "Indigo / Transparent": match whole values first.
      const fixed: string[] = [];
      for (const v of values) {
        const hit = opt.values.find((x) => norm(x) === norm(v));
        if (!hit) return { ok: false, error: `${opt.name} has no value "${v}"; values are ${opt.values.join(", ")}` };
        fixed.push(hit);
      }
      values = fixed;
    }
    out[name] = [...new Set([...(out[name] ?? []), ...values])];
  }
  return { ok: true, value: out };
}

// One line for one variant -------------------------------------------------------------------------

export type LineCell = {
  /** Quantity before scrap; 0 = not in this variant. */
  qty: number;
  componentId: string | null;
  /** What the line gives without an override. */
  baseQty: number;
  baseComponentId: string | null;
  overridden: boolean;
  /** The option value an option-mapped line looked up (for messages). */
  optionValue?: string;
};

export function lineCell(ix: BomIndex, line: BomLineRow, variantId: string | null, options: Options): LineCell {
  let baseComponentId = line.component_item_id;
  let value: string | undefined;
  if (line.option_name) {
    value = optionValue(options, line.option_name);
    baseComponentId = (value !== undefined ? ix.mapsByLine.get(line.id)?.get(norm(value)) : undefined) ?? line.component_item_id;
  }
  const baseQty = lineApplies(line, options) ? Number(line.qty) : 0;
  const ov = variantId ? ix.overridesByLine.get(line.id)?.get(variantId) : undefined;
  return {
    qty: ov?.qty != null ? Number(ov.qty) : baseQty,
    componentId: ov?.component_item_id ?? baseComponentId,
    baseQty,
    baseComponentId,
    overridden: !!ov && (ov.qty != null || ov.component_item_id != null),
    optionValue: value,
  };
}

// Resolution -------------------------------------------------------------------------------------

export type CostSource = "bom" | "std_cost" | "vendor" | "missing";

export type ResolvedLine = {
  /** Unique within one resolution (line ids along the path). */
  key: string;
  lineId: string;
  bomId: string;
  depth: number;
  /** Phantom sub-assemblies (item ids) this part came through, outermost first. */
  via: string[];
  itemId: string;
  item: BomItem;
  /** Per finished unit: the line's quantity with scrap, times every phantom parent's. */
  qty: number;
  supply: Supply;
  packaging: boolean;
  ref: string;
  /** A phantom sub-assembly: shown as a heading, its parts follow and carry the cost. */
  phantom: boolean;
  /** A sub-assembly (or sell item) with its own BOM, kept as one line and costed at its roll-up. */
  stocked: boolean;
  unitCost: number | null;
  costSource: CostSource;
  /** False when the cost leaves out parts without a cost (a sub-assembly's roll-up), or there is none. */
  costComplete: boolean;
  /** qty × unitCost; null for phantom headings and missing costs. */
  extCost: number | null;
  overridden: boolean;
};

export type CostSummary = {
  total: number;
  /** HHL buys these and ships them to the CM. */
  consigned: number;
  /** The CM buys or makes these, into its price. */
  cmBuys: number;
  /** Services (PCBA, assembly) by kind, whoever supplies them. */
  service: number;
  /** Charges --- the maker's per-unit charges (this family's and its phantoms'), in the total. */
  charges: number;
  /** Charges --- per-order charges: once per order, so not in a unit's total. */
  perOrder: number;
  /** Lines whose cost is missing or incomplete; total leaves them out. */
  missing: number;
  complete: boolean;
};

/** Charges --- one charge of the family resolved or of a phantom in it: `qty` of what it's charged on per unit. */
export type ResolvedCharge = {
  key: string;
  id: string;
  name: string;
  amount: number | null;
  per: ChargePer;
  layer: ChargeLayer;
  qty: number;
  /** The phantoms it's under (item ids), [] for the family's own. */
  via: string[];
};

export type Resolved = {
  bom: BomRow;
  variant: BomItem | undefined;
  options: Options;
  lines: ResolvedLine[];
  /** Charges --- the family's charges and its phantoms' (not stocked sub-assemblies': those are in their roll-up). */
  charges: ResolvedCharge[];
  cost: CostSummary;
  warnings: string[];
};

type Rollup = { cost: CostSummary; warnings: string[] };

/** Charges --- adds a family's charges, `qty` of it per unit, under `via`. */
function addCharges(ix: BomIndex, bom: BomRow, qty: number, via: string[], out: ResolvedCharge[]) {
  for (const c of ix.chargesByFamily.get(familyKey(bom)) ?? [])
    out.push({ key: [...via, c.id].join("/"), id: c.id, name: c.name, amount: num(c.amount), per: c.per, layer: c.layer, qty, via });
}

/** How messages name a line: "Line 3 (SHELL)" style, from what the line has. */
export const lineLabel = (ix: BomIndex, line: BomLineRow) => {
  const what = line.ref_designator || ix.itemById.get(line.component_item_id ?? "")?.sku || (line.option_name ? `by ${line.option_name}` : "");
  return `Line ${line.position}${what ? ` (${what})` : ""}`;
};

/** A family's flat BOM for one variant (a product's variant, or the sub-assembly for its own BOM). */
export function resolve(ix: BomIndex, bom: BomRow, variant: BomItem | undefined, opts: { options?: Options } = {}): Resolved {
  // Production runs --- a stocked sub-assembly resolved on its own sees the options of the variant it goes into.
  const options = opts.options ?? (bom.shopify_product_id ? variantOptions(variant) : {});
  const lines: ResolvedLine[] = [];
  const warnings: string[] = [];
  const stack = new Set<string>([bom.item_id ?? "", variant?.id ?? ""]);
  const charges: ResolvedCharge[] = [];
  addCharges(ix, bom, 1, [], charges);
  walk(ix, bom, variant?.id ?? null, options, 1, [], "", stack, lines, warnings, null, false, charges, bom.make_vendor_id);
  return { bom, variant, options, lines, charges, cost: summarize(lines, charges), warnings };
}

function walk(
  ix: BomIndex,
  bom: BomRow,
  variantId: string | null,
  options: Options,
  mult: number,
  via: string[],
  keyPrefix: string,
  stack: Set<string>,
  out: ResolvedLine[],
  warnings: string[],
  inheritSupply: Supply | null,
  inheritPackaging = false,
  charges: ResolvedCharge[] = [],
  /** Who builds what this level is in: a sub-assembly they also make (or with no maker) is built inside it. */
  maker: string | null = null,
) {
  for (const line of ix.linesByBom.get(bom.id) ?? []) {
    const cell = lineCell(ix, line, variantId, options);
    if (cell.qty <= 0) continue;
    if (!cell.componentId) {
      warnings.push(
        !line.option_name
          ? `${lineLabel(ix, line)} has no part`
          : cell.optionValue === undefined
            ? `${lineLabel(ix, line)} picks its part by ${line.option_name}, which this variant doesn't have`
            : `${lineLabel(ix, line)} has no part for ${line.option_name} = ${cell.optionValue}`,
      );
      continue;
    }
    const item = ix.itemById.get(cell.componentId);
    if (!item) {
      warnings.push("A line's part no longer exists");
      continue;
    }
    if (stack.has(item.id)) {
      warnings.push(`${item.sku || item.name} contains itself; left out`);
      continue;
    }
    const qty = cell.qty * (1 + Number(line.scrap_pct) / 100) * mult;
    const supply = inheritSupply ?? line.supply;
    const key = `${keyPrefix}${line.id}`;
    const base = {
      key,
      lineId: line.id,
      bomId: bom.id,
      depth: via.length,
      via,
      itemId: item.id,
      item,
      qty,
      supply,
      packaging: inheritPackaging || isPackagingLine(line, item), // Packaging --- a packaging kit's parts are packaging too
      ref: line.ref_designator,
      overridden: cell.overridden,
    };
    const sub = bomOf(ix, item);
    if (sub && builtHere(sub, item, supply, maker)) {
      const at = out.length;
      const chargedAt = charges.length;
      out.push({ ...base, phantom: true, stocked: false, unitCost: null, costSource: "bom", costComplete: true, extCost: null });
      const inner = new Set(stack).add(item.id);
      addCharges(ix, sub, qty, [...via, item.id], charges); // Charges --- its charges come with its parts
      // Its problems name it, as a stocked one's do ("SUB-PCBA: Line 6 (LED) has no part …").
      const inside: string[] = [];
      walk(
        ix,
        sub,
        null,
        options,
        qty,
        [...via, item.id],
        `${key}/`,
        inner,
        out,
        inside,
        supply === "cm_buys" ? "cm_buys" : null,
        base.packaging,
        charges,
        maker,
      );
      for (const w of inside) warnings.push(`${item.sku || item.name}: ${w}`);
      // The heading shows what one of it costs, from the parts (and charges) just added under it.
      const parts = summarize(out.slice(at + 1), charges.slice(chargedAt));
      out[at] = { ...out[at], unitCost: qty ? parts.total / qty : 0, costComplete: parts.complete };
      continue;
    }
    const cost = unitCost(ix, item, sub, options, new Set(stack).add(item.id), warnings);
    out.push({
      ...base,
      phantom: false,
      stocked: !!sub,
      unitCost: cost.unit,
      costSource: cost.source,
      costComplete: cost.complete,
      extCost: cost.unit == null ? null : cost.unit * qty,
    });
  }
}

function unitCost(
  ix: BomIndex,
  item: BomItem,
  sub: BomRow | undefined,
  options: Options,
  stack: Set<string>,
  warnings: string[],
): { unit: number | null; source: CostSource; complete: boolean } {
  if (sub) {
    // A sell variant costs as built for its own options; a sub-assembly sees the variant it goes into.
    const opts = item.source === "shopify" ? variantOptions(item) : options;
    const cacheKey = `${sub.id}|${item.id}|${JSON.stringify(opts)}`;
    let r = ix.rollups.get(cacheKey);
    if (!r) {
      const lines: ResolvedLine[] = [];
      const inner: string[] = [];
      const charges: ResolvedCharge[] = [];
      addCharges(ix, sub, 1, [], charges);
      walk(ix, sub, item.source === "shopify" ? item.id : null, opts, 1, [], "", stack, lines, inner, null, false, charges, sub.make_vendor_id);
      r = { cost: summarize(lines, charges), warnings: inner };
      ix.rollups.set(cacheKey, r);
    }
    for (const w of r.warnings) warnings.push(`${item.sku || item.name}: ${w}`);
    return { unit: r.cost.total, source: "bom", complete: r.cost.complete };
  }
  if (item.std_cost != null) return { unit: Number(item.std_cost), source: "std_cost", complete: true };
  const v = ix.vendorPrice.get(item.id);
  if (v != null) return { unit: v, source: "vendor", complete: true };
  return { unit: null, source: "missing", complete: false };
}

function summarize(lines: ResolvedLine[], charges: readonly ResolvedCharge[] = []): CostSummary {
  const s: CostSummary = { total: 0, consigned: 0, cmBuys: 0, service: 0, charges: 0, perOrder: 0, missing: 0, complete: true };
  for (const c of charges) {
    if (c.amount == null) {
      s.missing++;
      s.complete = false;
    } else if (c.per === "order") s.perOrder += c.amount;
    else s.charges += c.amount * c.qty;
  }
  s.total += s.charges;
  for (const l of lines) {
    if (l.phantom) continue;
    if (l.extCost == null || !l.costComplete) {
      s.missing++;
      s.complete = false;
    }
    if (l.extCost == null) continue;
    s.total += l.extCost;
    if (l.item.kind === "service") s.service += l.extCost;
    else if (byCm(l.supply)) s.cmBuys += l.extCost;
    else s.consigned += l.extCost;
  }
  return s;
}

/** Totals per component across a resolved BOM (what planning and make orders need). */
export function flatten(resolved: Resolved): { item: BomItem; qty: number; supply: Supply; unitCost: number | null; extCost: number | null }[] {
  const out = new Map<string, { item: BomItem; qty: number; supply: Supply; unitCost: number | null; extCost: number | null }>();
  for (const l of resolved.lines) {
    if (l.phantom) continue;
    const key = `${l.itemId}|${l.supply}`;
    const row = out.get(key);
    if (row) {
      row.qty += l.qty;
      row.extCost = row.extCost == null || l.extCost == null ? null : row.extCost + l.extCost;
    } else out.set(key, { item: l.item, qty: l.qty, supply: l.supply, unitCost: l.unitCost, extCost: l.extCost });
  }
  return [...out.values()].sort((a, b) => bySku(a.item, b.item));
}

// Family roll-up (dashboard) -----------------------------------------------------------------------

export type Range = { min: number; max: number } | null;

export type FamilySummary = {
  bom: BomRow;
  key: string;
  label: string;
  variants: { variant: BomItem; resolved: Resolved; margin: number | null; build: Buildable }[];
  cost: Range;
  /** Units buildable from stock now, over the variants (null when nothing in it is stocked). */
  buildable: Range;
  /** What stops the variant that can build the fewest. */
  limiting: Buildable["limiting"];
  price: Range;
  /** (price − cost) / price */
  margin: Range;
  flags: {
    draft: boolean;
    /** Parts with no cost (or a sub-assembly whose roll-up has gaps). */
    missingCost: BomItem[];
    /** Parts HHL buys with no vendor. */
    missingVendor: BomItem[];
    noMakeVendor: boolean;
    /** Parts marked end of life or obsolete. */
    endOfLife: BomItem[];
    warnings: string[];
  };
};

const range = (values: number[]): Range => (values.length ? { min: Math.min(...values), max: Math.max(...values) } : null);

export function summarizeFamily(ix: BomIndex, bom: BomRow): FamilySummary {
  const missingCost = new Map<string, BomItem>();
  const missingVendor = new Map<string, BomItem>();
  const endOfLife = new Map<string, BomItem>();
  const warnings = new Set<string>();
  const variants = variantsOf(ix, bom).map((variant) => {
    const resolved = resolve(ix, bom, variant);
    for (const w of resolved.warnings) warnings.add(w);
    for (const l of resolved.lines) {
      if (l.item.lifecycle === "eol" || l.item.lifecycle === "obsolete") endOfLife.set(l.itemId, l.item);
      if (l.phantom) continue;
      if (l.extCost == null || !l.costComplete) missingCost.set(l.itemId, l.item);
      if (!l.stocked && l.supply === "hhl_consigns" && !ix.hasVendor.has(l.itemId)) missingVendor.set(l.itemId, l.item);
    }
    const price = variant.price == null ? null : Number(variant.price);
    const margin = price && resolved.cost.complete ? (price - resolved.cost.total) / price : null;
    return { variant, resolved, margin, build: buildable(ix, bom, variant) };
  });
  const complete = variants.filter((v) => v.resolved.cost.complete);
  const builds = variants.flatMap((v) => (v.build.qty == null ? [] : [v]));
  const fewest = builds.reduce<(typeof builds)[number] | undefined>((a, v) => (!a || (v.build.qty ?? 0) < (a.build.qty ?? 0) ? v : a), undefined);
  return {
    bom,
    key: familyKey(bom),
    label: ownerLabel(ix, bom),
    variants,
    cost: range(variants.map((v) => v.resolved.cost.total)),
    buildable: range(builds.map((v) => v.build.qty ?? 0)),
    limiting: fewest?.build.limiting ?? null,
    price: bom.shopify_product_id ? range(variants.flatMap((v) => (v.variant.price == null ? [] : [Number(v.variant.price)]))) : null,
    margin: range(complete.flatMap((v) => (v.margin == null ? [] : [v.margin]))),
    flags: {
      draft: bom.status === "draft",
      missingCost: [...missingCost.values()].sort(bySku),
      missingVendor: [...missingVendor.values()].sort(bySku),
      noMakeVendor: !bom.make_vendor_id && !bom.phantom,
      endOfLife: [...endOfLife.values()].sort(bySku),
      warnings: [...warnings],
    },
  };
}

/** Every family once, at its current revision. */
export function families(ix: BomIndex): BomRow[] {
  return [...ix.families.keys()].flatMap((key) => currentRevision(ix, key) ?? []);
}

// Where-used ---------------------------------------------------------------------------------------

export type Usage = {
  bom: BomRow;
  label: string;
  /** Quantity per unit of the parent (before scrap), over the variants that use it. */
  qty: Range;
  /** Variants that use it, of how many (a sub-assembly's own BOM counts as 1 of 1). */
  variants: number;
  of: number;
  /** For sub-assemblies: where the parent is used in turn. */
  parents: Usage[];
};

/** Where an item is used, through sub-assemblies to the products that finally use it (current revisions). */
export function whereUsed(ix: BomIndex, itemId: string, seen: Set<string> = new Set([itemId])): Usage[] {
  const out: Usage[] = [];
  for (const bom of families(ix)) {
    const variants = variantsOf(ix, bom);
    const qtys: number[] = [];
    let using = 0;
    for (const v of variants) {
      const options = bom.shopify_product_id ? variantOptions(v) : {};
      let n = 0;
      for (const line of ix.linesByBom.get(bom.id) ?? []) {
        const cell = lineCell(ix, line, v.id, options);
        if (cell.componentId === itemId && cell.qty > 0) n += cell.qty;
      }
      if (n > 0) {
        using++;
        qtys.push(n);
      }
    }
    if (!using) continue;
    const owner = bom.item_id ?? "";
    const parents = bom.item_id && !seen.has(owner) ? whereUsed(ix, owner, new Set(seen).add(owner)) : [];
    out.push({ bom, label: ownerLabel(ix, bom), qty: range(qtys), variants: using, of: variants.length, parents });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}

/** Items a family uses directly (lines, option maps, overrides), for the picker and cycle checks. */
export function componentsOf(ix: BomIndex, bomId: string): Set<string> {
  const out = new Set<string>();
  for (const line of ix.linesByBom.get(bomId) ?? []) {
    if (line.component_item_id) out.add(line.component_item_id);
    for (const c of ix.mapsByLine.get(line.id)?.values() ?? []) out.add(c);
    for (const o of ix.overridesByLine.get(line.id)?.values() ?? []) if (o.component_item_id) out.add(o.component_item_id);
  }
  return out;
}

// Buildable --------------------------------------------------------------------------------------

/**
 * What there is of an item to build with (BomData.stock): at HHL, Shopify items' available (on hand less units on open
 * orders) plus local items' stock_balance (the ledger), over the locations that count (see countedStock()); a CM's
 * own index (make.ts) feeds it the CM's stock. The one place that reads it.
 */
export function onHandFor(ix: BomIndex, itemId: string): number {
  return ix.onHand.get(itemId) ?? 0;
}

export type Buildable = {
  /** Whole units that can be built from stock now; null when HHL supplies nothing in it (CM parts and services only). */
  qty: number | null;
  /** The part that runs out first: what one unit takes of it (through every level) and what's on hand. */
  limiting: { item: BomItem; perUnit: number; onHand: number } | null;
  /** A line without a part: nothing can be built until it has one. */
  blocked: string | null;
};

const EPS = 1e-9;

/**
 * What building `n` of a variant takes from HHL's stock, through every level: a sub-assembly (and a sell item with a
 * BOM) comes from its own stock first and the rest is built from its parts. Parts the CM buys or makes and services aren't HHL's to have, so they never limit (a sub-assembly the CM
 * makes in the build is its parts).
 */
function demand(ix: BomIndex, bom: BomRow, variant: BomItem | undefined, n: number) {
  const need = new Map<string, number>();
  let blocked: string | null = null;
  const explode = (b: BomRow, variantId: string | null, options: Options, mult: number, stack: Set<string>) => {
    for (const line of ix.linesByBom.get(b.id) ?? []) {
      const cell = lineCell(ix, line, variantId, options);
      if (cell.qty <= 0 || line.supply === "cm_buys") continue;
      const item = cell.componentId ? ix.itemById.get(cell.componentId) : undefined;
      if (!item) {
        blocked ??= `${lineLabel(ix, line)} has no part${cell.optionValue ? ` for ${line.option_name} = ${cell.optionValue}` : ""}`;
        continue;
      }
      if (stack.has(item.id) || item.kind === "service") continue;
      const q = cell.qty * (1 + Number(line.scrap_pct) / 100) * mult;
      const sub = bomOf(ix, item);
      const inner = new Set(stack).add(item.id);
      // One the CM makes in the build is its parts; any other sub-assembly comes from its own stock first (below).
      if (sub && item.source === "local" && line.supply === "cm_makes") {
        explode(sub, null, options, q, inner);
        continue;
      }
      if (line.supply === "cm_makes") continue; // the CM makes it in-house: not HHL's to have
      if (sub && ix.linesByBom.get(sub.id)?.length) {
        const own = Math.min(q, Math.max(0, onHandFor(ix, item.id) - (need.get(item.id) ?? 0)));
        if (own > 0) need.set(item.id, (need.get(item.id) ?? 0) + own);
        const sell = item.source === "shopify";
        if (q - own > EPS) explode(sub, sell ? item.id : null, sell ? variantOptions(item) : options, q - own, inner);
        continue;
      }
      need.set(item.id, (need.get(item.id) ?? 0) + q);
    }
  };
  explode(bom, variant?.id ?? null, bom.shopify_product_id ? variantOptions(variant) : {}, n, new Set([bom.item_id ?? "", variant?.id ?? ""]));
  return { need, blocked };
}

/** How many of a variant (or a sub-assembly) can be built from what's on hand now, and what runs out first. */
export function buildable(ix: BomIndex, bom: BomRow, variant: BomItem | undefined): Buildable {
  const first = demand(ix, bom, variant, 1);
  if (first.blocked) return { qty: 0, limiting: null, blocked: first.blocked };
  if (!first.need.size) return { qty: null, limiting: null, blocked: null };
  const fits = (n: number) => {
    for (const [id, q] of demand(ix, bom, variant, n).need) if (q > onHandFor(ix, id) + EPS) return false;
    return true;
  };
  // Double until it doesn't fit, then halve the gap: lo fits, hi doesn't.
  let lo = 0;
  let hi = 1;
  while (hi <= 1e9 && fits(hi)) [lo, hi] = [hi, hi * 2];
  while (hi - lo > 1) {
    const mid = Math.floor((lo + hi) / 2);
    if (fits(mid)) lo = mid;
    else hi = mid;
  }
  // The limiting part: of the ones a unit more would run short of, the one with least of what it needs.
  let limiting: Buildable["limiting"] = null;
  let worst = Number.POSITIVE_INFINITY;
  for (const [id, q] of demand(ix, bom, variant, lo + 1).need) {
    const have = onHandFor(ix, id);
    const item = ix.itemById.get(id);
    if (!item || q <= have + EPS) continue;
    const ratio = have / q;
    if (ratio < worst - EPS || (Math.abs(ratio - worst) <= EPS && limiting && bySku(item, limiting.item) < 0)) {
      worst = ratio;
      limiting = { item, perUnit: q / (lo + 1), onHand: have };
    }
  }
  return { qty: lo, limiting, blocked: null };
}

// Compare ----------------------------------------------------------------------------------------

export type CompareSide = { qty: number; supply: Supply; unitCost: number | null; extCost: number | null };
export type CompareChange = "added" | "removed" | "qty" | "supply" | "cost" | "same";
export type CompareRow = {
  item: BomItem;
  change: CompareChange;
  a: CompareSide | null;
  b: CompareSide | null;
  /** B − A in extended cost; null when a side has no cost. */
  delta: number | null;
};
export type Comparison = {
  rows: CompareRow[];
  a: CostSummary;
  b: CostSummary;
  counts: Record<Exclude<CompareChange, "same">, number>;
};

const CHANGE_ORDER: Record<CompareChange, number> = { added: 0, removed: 1, qty: 2, supply: 3, cost: 4, same: 5 };
const differs = (x: number | null, y: number | null) => (x == null) !== (y == null) || (x != null && y != null && Math.abs(x - y) > EPS);

/**
 * Two flat BOMs part by part (phantoms exploded, stocked sub-assemblies as one part): what B adds,
 * drops or changes against A. Use it for two variants of a family, or one variant in two revisions.
 */
export function compareResolved(a: Resolved, b: Resolved): Comparison {
  const parts = (r: Resolved) => {
    const out = new Map<string, { item: BomItem; side: CompareSide }>();
    for (const l of r.lines) {
      if (l.phantom) continue;
      const row = out.get(l.itemId);
      if (!row) out.set(l.itemId, { item: l.item, side: { qty: l.qty, supply: l.supply, unitCost: l.unitCost, extCost: l.extCost } });
      else {
        row.side.qty += l.qty;
        row.side.extCost = row.side.extCost == null || l.extCost == null ? null : row.side.extCost + l.extCost;
      }
    }
    return out;
  };
  const [pa, pb] = [parts(a), parts(b)];
  const counts = { added: 0, removed: 0, qty: 0, supply: 0, cost: 0 };
  const rows: CompareRow[] = [];
  for (const id of new Set([...pa.keys(), ...pb.keys()])) {
    const [x, y] = [pa.get(id)?.side ?? null, pb.get(id)?.side ?? null];
    let change: CompareChange = "same";
    if (!x) change = "added";
    else if (!y) change = "removed";
    else if (Math.abs(x.qty - y.qty) > EPS) change = "qty";
    else if (x.supply !== y.supply) change = "supply";
    else if (differs(x.unitCost, y.unitCost)) change = "cost";
    if (change !== "same") counts[change]++;
    const [ea, eb] = [x ? x.extCost : 0, y ? y.extCost : 0];
    rows.push({ item: (pa.get(id) ?? pb.get(id))?.item as BomItem, change, a: x, b: y, delta: ea == null || eb == null ? null : eb - ea });
  }
  rows.sort((r, s) => CHANGE_ORDER[r.change] - CHANGE_ORDER[s.change] || bySku(r.item, s.item));
  return { rows, a: a.cost, b: b.cost, counts };
}

// Replace a part everywhere ------------------------------------------------------------------------

export type ReplaceTarget = {
  key: string;
  label: string;
  /** Where the change goes: the family's newest draft (changed in place), or the revision in use (copied into a new draft first). */
  bom: BomRow;
  action: "edit_draft" | "new_revision";
  /** The revision in use now, when it isn't `bom`. */
  current: BomRow | undefined;
  /** Lines that name the part (as their part, in an option map or in an override). */
  lines: number;
  /** Variants that end up using it, of how many. */
  variants: number;
  of: number;
};

const linesNaming = (ix: BomIndex, bomId: string, itemId: string) =>
  (ix.linesByBom.get(bomId) ?? []).filter(
    (l) =>
      l.component_item_id === itemId ||
      [...(ix.mapsByLine.get(l.id)?.values() ?? [])].includes(itemId) ||
      [...(ix.overridesByLine.get(l.id)?.values() ?? [])].some((o) => o.component_item_id === itemId),
  );

/** The BOMs a part can be swapped out of, one per family. An active revision is never a target itself. */
export function replaceTargets(ix: BomIndex, itemId: string): ReplaceTarget[] {
  const out: ReplaceTarget[] = [];
  for (const [key, revs] of ix.families) {
    const draft = revs.find((b) => b.status === "draft");
    const current = currentRevision(ix, key);
    const bom = draft ?? (current?.status === "active" ? current : undefined);
    if (!bom) continue;
    const lines = linesNaming(ix, bom.id, itemId);
    if (!lines.length) continue;
    const variants = variantsOf(ix, bom);
    const using = variants.filter((v) => {
      const options = bom.shopify_product_id ? variantOptions(v) : {};
      return lines.some((l) => {
        const c = lineCell(ix, l, v.id, options);
        return c.componentId === itemId && c.qty > 0;
      });
    }).length;
    out.push({
      key,
      label: ownerLabel(ix, bom),
      bom,
      action: draft ? "edit_draft" : "new_revision",
      current: current && current.id !== bom.id ? current : undefined,
      lines: lines.length,
      variants: using,
      of: variants.length,
    });
  }
  return out.sort((a, b) => a.label.localeCompare(b.label));
}
